import { useMemo, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Button, toast, Modal } from '@uniquegood/realworld-studio-design';
import { css } from '@emotion/react';
import Card from './components/Card';
import Layout from './components/Layout';
import CheckList from './components/CheckList';

import {
  Genre,
  TemporalBackground,
  SpatialBackground,
  Atmosphere,
  StoryGoal,
  MainEvent,
  Ending,
  CreateProjectStoryForm,
  CreateProjectStoryAPI,
  GENRE_OPTIONS,
  TEMPORAL_BACKGROUND_OPTIONS,
  SPATIAL_BACKGROUND_OPTIONS,
  ATMOSPHERE_OPTIONS,
  STORY_GOAL_OPTIONS,
  MAIN_EVENT_OPTIONS,
  ENDING_OPTIONS
} from './types';
import {
  getGenreKoreanName,
  getTemporalBackgroundKoreanName,
  getSpatialBackgroundKoreanName,
  getAtmosphereKoreanName,
  getStoryGoalKoreanName,
  getMainEventKoreanName,
  getEndingKoreanName
} from './utils/getGenreKoreanName';
import {
  useParamsWithMatchPath,
  usePostRequest,
  useModalState,
  usePageExit
} from '@/hooks';
import LoadingTemplate from './components/LoadingTemplate';

import ErrorTemplate from './components/ErrorTemplate';
import { ClickEventName, PageViewEventName, track } from '@/track';

function AiConfig() {
  const { appId } = useParamsWithMatchPath();
  const history = useHistory();
  const { modal, openModal, closeModal } = useModalState();

  useEffect(() => {
    track.onPageViewMixpanel(PageViewEventName.view_newgame_ai_storyconfig, {
      channel_id: appId
    });
  }, [appId]);

  const {
    trigger: createStory,
    isLoading: isCreating,
    error: createError
  } = usePostRequest<CommonResponseModel<CreateProjectStoryResponseModel>>(
    `/apps/${appId}/ai/stories`
  );

  // React Hook Form 설정
  const { watch, setValue, handleSubmit } = useForm<CreateProjectStoryForm>({
    defaultValues: {
      genre: null,
      temporalBackground: null,
      spatialBackground: null,
      atmosphere: [],
      storyGoal: null,
      storyGoalCustom: '',
      mainEvent: null,
      mainEventCustom: '',
      ending: null,
      purpose: ''
    }
  });

  // 현재 선택된 값들 감시 (한 번만 호출로 최적화)
  const selectedValues = watch();

  // 페이지 이탈 경고 모달 열기 함수
  const openUnsavedChangesWarningModal = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      track.onPageViewMixpanel(PageViewEventName.view_newgame_ai_exit_modal, {
        channel_id: appId
      });
      openModal({
        title: '작성한 내용이 사라질 수 있어요',
        children: (
          <div>
            콘텐츠를 만드는 도중에 페이지를 벗어나면 저장되지 않아요.
            <br />
            정말 나가시겠어요?
          </div>
        ),
        onClose: () => {
          closeModal();
          resolve(false); // 모달이 닫히면 false 반환 (남아있기)
        },
        customActionButton: (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              type="basic"
              size="large"
              onClick={() => {
                closeModal();
                track.onClick({
                  clickEventName: ClickEventName.click_newgame_ai_exit_modal_exit,
                  params: {
                    channel_id: appId
                  }
                });
                resolve(true); // 나가기 버튼 클릭 시 true 반환
                setTimeout(() => {
                  history.push(`/apps/${appId}`);
                }, 0);
              }}
            >
              나가기
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                track.onClick({
                  clickEventName: ClickEventName.click_newgame_ai_exit_modal_cancel,
                  params: {
                    channel_id: appId
                  }
                });
                closeModal();
                resolve(false); // 남아있기 버튼 클릭 시 false 반환
              }}
            >
              남아있기
            </Button>
          </div>
        )
      });
    });
  }, [openModal, closeModal, appId]);

  // 페이지 이탈 시 실행할 로직
  const { allowExit } = usePageExit(
    () => {
      console.log('AI Config 페이지를 떠납니다');
    },
    {
      onBeforeUnload: () => console.log('새로고침/브라우저 종료'),
      onVisibilityChange: () => console.log('탭 변경/브라우저 숨김'),
      onRouteChange: () => console.log('다른 페이지로 라우팅'),
      shouldPreventDefault: true,
      shouldBlockRouting: true,
      enabled: true,
      customConfirmModal: openUnsavedChangesWarningModal,
      exitPath: `/apps/${appId}`
    }
  );

  const {
    genre: selectedGenre,
    temporalBackground: selectedTemporalBackground,
    spatialBackground: selectedSpatialBackground,
    atmosphere: selectedAtmospheres,
    storyGoal: selectedStoryGoal,
    storyGoalCustom: selectedStoryGoalCustom,
    mainEvent: selectedMainEvent,
    mainEventCustom: selectedMainEventCustom,
    ending: selectedEnding,
    purpose: selectedPurpose
  } = selectedValues;

  // 폼 유효성 검증 (useMemo로 최적화)
  const isFormValid = useMemo(
    () =>
      selectedGenre !== null &&
      selectedTemporalBackground !== null &&
      selectedSpatialBackground !== null &&
      selectedAtmospheres.length > 0 &&
      selectedStoryGoal !== null &&
      (selectedStoryGoal !== StoryGoal.Custom || selectedStoryGoalCustom?.trim() !== '') &&
      selectedMainEvent !== null &&
      (selectedMainEvent !== MainEvent.Custom || selectedMainEventCustom?.trim() !== '') &&
      selectedEnding !== null,
    [
      selectedGenre,
      selectedTemporalBackground,
      selectedSpatialBackground,
      selectedAtmospheres,
      selectedStoryGoal,
      selectedStoryGoalCustom,
      selectedMainEvent,
      selectedMainEventCustom,
      selectedEnding
    ]
  );

  // 장르 선택/해제 핸들러 (단일 선택)
  const handleGenreSelect = useCallback(
    (genre: Genre) => {
      const isSelected = selectedGenre === genre;
      setValue('genre', isSelected ? null : genre);
    },
    [selectedGenre, setValue]
  );

  // 시대 배경 선택/해제 핸들러 (단일 선택)
  const handleTemporalBackgroundSelect = useCallback(
    (temporalBackground: TemporalBackground) => {
      const isSelected = selectedTemporalBackground === temporalBackground;
      setValue('temporalBackground', isSelected ? null : temporalBackground);
    },
    [selectedTemporalBackground, setValue]
  );

  // 공간 배경 선택/해제 핸들러 (단일 선택)
  const handleSpatialBackgroundSelect = useCallback(
    (spatialBackground: SpatialBackground) => {
      const isSelected = selectedSpatialBackground === spatialBackground;
      setValue('spatialBackground', isSelected ? null : spatialBackground);
    },
    [selectedSpatialBackground, setValue]
  );

  // 분위기 선택/해제 핸들러 (최대 2개 선택 가능)
  const handleAtmosphereSelect = useCallback(
    (atmosphere: Atmosphere) => {
      const currentAtmospheres = selectedAtmospheres;
      const isSelected = currentAtmospheres.includes(atmosphere);

      if (isSelected) {
        // 선택 해제
        setValue(
          'atmosphere',
          currentAtmospheres.filter((a) => a !== atmosphere)
        );
      } else if (currentAtmospheres.length < 2) {
        // 선택 추가 (최대 2개까지)
        setValue('atmosphere', [...currentAtmospheres, atmosphere]);
      }
    },
    [selectedAtmospheres, setValue]
  );

  // 스토리 목표 선택/해제 핸들러 (단일 선택)
  const handleStoryGoalSelect = useCallback(
    (storyGoal: StoryGoal) => {
      const isSelected = selectedStoryGoal === storyGoal;

      if (isSelected) {
        // 선택 해제
        setValue('storyGoal', null);
        // 직접 입력 텍스트는 유지 (사용자가 실수로 선택 해제한 경우를 고려)
      } else {
        // 새로 선택
        setValue('storyGoal', storyGoal);
        if (storyGoal !== StoryGoal.Custom) {
          // 다른 옵션을 선택한 경우에만 직접 입력 초기화
          setValue('storyGoalCustom', '');
        }
      }
    },
    [selectedStoryGoal, setValue]
  );

  // 중심 사건 선택/해제 핸들러 (단일 선택)
  const handleMainEventSelect = useCallback(
    (mainEvent: MainEvent) => {
      const isSelected = selectedMainEvent === mainEvent;

      if (isSelected) {
        // 선택 해제
        setValue('mainEvent', null);
        // 직접 입력 텍스트는 유지 (사용자가 실수로 선택 해제한 경우를 고려)
      } else {
        // 새로 선택
        setValue('mainEvent', mainEvent);
        if (mainEvent !== MainEvent.Custom) {
          // 다른 옵션을 선택한 경우에만 직접 입력 초기화
          setValue('mainEventCustom', '');
        }
      }
    },
    [selectedMainEvent, setValue]
  );

  // 결말 선택/해제 핸들러 (단일 선택)
  const handleEndingSelect = useCallback(
    (ending: Ending) => {
      const isSelected = selectedEnding === ending;
      setValue('ending', isSelected ? null : ending);
    },
    [selectedEnding, setValue]
  );

  // 선택 상태 확인 헬퍼 함수들 (useCallback으로 메모이제이션)
  const isGenreSelected = useCallback((genre: Genre) => selectedGenre === genre, [selectedGenre]);
  const isTemporalBackgroundSelected = useCallback(
    (temporalBackground: TemporalBackground) => selectedTemporalBackground === temporalBackground,
    [selectedTemporalBackground]
  );
  const isSpatialBackgroundSelected = useCallback(
    (spatialBackground: SpatialBackground) => selectedSpatialBackground === spatialBackground,
    [selectedSpatialBackground]
  );
  const isAtmosphereSelected = useCallback(
    (atmosphere: Atmosphere) => selectedAtmospheres.includes(atmosphere),
    [selectedAtmospheres]
  );
  const isStoryGoalSelected = useCallback(
    (storyGoal: StoryGoal) => selectedStoryGoal === storyGoal,
    [selectedStoryGoal]
  );
  const isMainEventSelected = useCallback(
    (mainEvent: MainEvent) => selectedMainEvent === mainEvent,
    [selectedMainEvent]
  );
  const isEndingSelected = useCallback(
    (ending: Ending) => selectedEnding === ending,
    [selectedEnding]
  );

  // 분위기를 하나의 문자열로 결합하는 함수 (useMemo로 최적화)
  const getAtmosphereString = useCallback((atmospheres: Atmosphere[]): string => {
    if (atmospheres.length === 0) return '';
    if (atmospheres.length === 1) return atmospheres[0];
    return atmospheres.join(',');
  }, []);

  // 폼 제출 핸들러 (useCallback으로 메모이제이션)
  const onSubmit = useCallback(
    async (data: CreateProjectStoryForm) => {
      try {
        // API 전송용 데이터로 변환
        const apiData: CreateProjectStoryAPI = {
          genre: data.genre!,
          temporalBackground: data.temporalBackground!,
          spatialBackground: data.spatialBackground!,
          atmosphere: getAtmosphereString(data.atmosphere),
          storyGoal: data.storyGoal === StoryGoal.Custom ? data.storyGoalCustom : data.storyGoal!,
          mainEvent: data.mainEvent === MainEvent.Custom ? data.mainEventCustom : data.mainEvent!,
          ending: data.ending!,
          purpose: data.purpose
        };

        // POST 요청 실행

        const result = await createStory(apiData);

        sessionStorage.setItem('aiStory', JSON.stringify(result));

        // 제출 시에는 의도적인 페이지 이동이므로 allowExit을 호출하여 페이지 이탈 허용
        allowExit();

        history.push(`/apps/${appId}/ai/storyConfirm`);
      } catch (error) {
        console.log(createError);
        console.error('스토리 생성 실패:', error);
        toast({
          type: 'error',
          message:
            (createError?.response?.data as { errorTitle: string }).errorTitle ||
            '스토리 생성에 실패했습니다. 다시 시도해주세요.'
        });
      }
    },
    [getAtmosphereString, createStory]
  );

  if (isCreating) {
    return (
      <>
        <LoadingTemplate />
        <Modal {...modal} />
      </>
    );
  }

  if (createError) {
    return (
      <>
        <ErrorTemplate onRetry={handleSubmit(onSubmit)} />
        <Modal {...modal} />
      </>
    );
  }

  return (
    <Layout
      title="어떤 콘텐츠를 만들까요?"
      description="원하는 콘텐츠를 선택하여 쉽고 빠르게 만들어보세요."
      progress={33.33}
    >
      <div css={Container}>
        {/* 장르 선택 섹션 */}
        <div css={CardContainer}>
          <Card
            title="장르는 무엇인가요?"
            description="전체 스토리의 방향성을 잡는 핵심 키워드를 설정해요."
          >
            <div css={CardBody}>
              {GENRE_OPTIONS.map((genre) => (
                <Button
                  key={genre}
                  type={isGenreSelected(genre) ? 'primary' : 'outlineBasic'}
                  size="small"
                  onClick={() => handleGenreSelect(genre)}
                >
                  {getGenreKoreanName(genre)}
                </Button>
              ))}
            </div>
          </Card>

          {/* 스토리 배경 섹션 */}
          <Card title="스토리의 배경을 알려주세요" description="스토리와 이미지 톤을 설정해요.">
            {/* 시대 선택 섹션 */}
            <div css={Section}>
              <div css={SectionTitle}>어느 시대에서 일어나나요?</div>
              <div css={ButtonGroup}>
                {TEMPORAL_BACKGROUND_OPTIONS.map((temporalBackground) => (
                  <Button
                    key={temporalBackground}
                    type={
                      isTemporalBackgroundSelected(temporalBackground) ? 'primary' : 'outlineBasic'
                    }
                    size="small"
                    onClick={() => handleTemporalBackgroundSelect(temporalBackground)}
                  >
                    {getTemporalBackgroundKoreanName(temporalBackground)}
                  </Button>
                ))}
              </div>
            </div>

            {/* 장소 선택 섹션 */}
            <div css={Section} style={{ marginTop: '16px' }}>
              <div css={SectionTitle}>어떤 장소에서 일어나나요?</div>
              <div css={ButtonGroup}>
                {SPATIAL_BACKGROUND_OPTIONS.map((spatialBackground) => (
                  <Button
                    key={spatialBackground}
                    type={
                      isSpatialBackgroundSelected(spatialBackground) ? 'primary' : 'outlineBasic'
                    }
                    size="small"
                    onClick={() => handleSpatialBackgroundSelect(spatialBackground)}
                  >
                    {getSpatialBackgroundKoreanName(spatialBackground)}
                  </Button>
                ))}
              </div>
            </div>
          </Card>

          {/* 분위기 선택 섹션 */}
          <Card
            title="전반적인 분위기는 어떤가요?"
            description="이미지·BGM·대사 톤을 설정해요. (최대 2개)"
          >
            <div css={ButtonGroup}>
              {ATMOSPHERE_OPTIONS.map((atmosphere) => (
                <Button
                  key={atmosphere}
                  type={isAtmosphereSelected(atmosphere) ? 'primary' : 'outlineBasic'}
                  size="small"
                  onClick={() => handleAtmosphereSelect(atmosphere)}
                  disabled={!isAtmosphereSelected(atmosphere) && selectedAtmospheres.length >= 2}
                >
                  {getAtmosphereKoreanName(atmosphere)}
                </Button>
              ))}
            </div>
          </Card>

          {/* 스토리 목표 선택 섹션 */}
          <Card
            title="스토리의 목표는 무엇인가요?"
            description="플레이어가 진행하며 달성해야 하는 목표를 설정해요."
          >
            <div css={Section}>
              <div css={ButtonGroup}>
                {STORY_GOAL_OPTIONS.map((storyGoal) => (
                  <Button
                    key={storyGoal}
                    type={isStoryGoalSelected(storyGoal) ? 'primary' : 'outlineBasic'}
                    size="small"
                    onClick={() => handleStoryGoalSelect(storyGoal)}
                  >
                    {getStoryGoalKoreanName(storyGoal)}
                  </Button>
                ))}
              </div>

              {/* 직접 입력 필드 - "직접 입력" 선택 시에만 표시 */}
              {selectedStoryGoal === StoryGoal.Custom && (
                <input
                  type="text"
                  placeholder="스토리 목표를 입력해주세요"
                  value={selectedStoryGoalCustom}
                  onChange={(e) => setValue('storyGoalCustom', e.target.value)}
                  css={PurposeInputField}
                />
              )}
            </div>
          </Card>

          {/* 중심 사건 선택 섹션 */}
          <Card
            title="중심 사건은 무엇인가요?"
            description="목표를 가로막는 사건이나 문제를 설정해요."
          >
            <div css={Section}>
              <div css={ButtonGroup}>
                {MAIN_EVENT_OPTIONS.map((mainEvent) => (
                  <Button
                    key={mainEvent}
                    type={isMainEventSelected(mainEvent) ? 'primary' : 'outlineBasic'}
                    size="small"
                    onClick={() => handleMainEventSelect(mainEvent)}
                  >
                    {getMainEventKoreanName(mainEvent)}
                  </Button>
                ))}
              </div>

              {/* 직접 입력 필드 - "직접 입력" 선택 시에만 표시 */}
              {selectedMainEvent === MainEvent.Custom && (
                <input
                  type="text"
                  placeholder="중심 사건을 입력해주세요"
                  value={selectedMainEventCustom}
                  onChange={(e) => setValue('mainEventCustom', e.target.value)}
                  css={PurposeInputField}
                />
              )}
            </div>
          </Card>

          {/* 결말 선택 섹션 */}
          <Card title="결말은 어떤가요?" description="">
            <div css={ButtonGroup}>
              {ENDING_OPTIONS.map((ending) => (
                <Button
                  key={ending}
                  type={isEndingSelected(ending) ? 'primary' : 'outlineBasic'}
                  size="small"
                  onClick={() => handleEndingSelect(ending)}
                >
                  {getEndingKoreanName(ending)}
                </Button>
              ))}
            </div>
          </Card>

          {/* 콘텐츠 제작 목적 섹션 (선택사항) */}
          <Card
            title={
              <div
                style={{
                  color: '#0E1017',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '24px'
                }}
              >
                {' '}
                콘텐츠 제작 목적을 알려주세요{' '}
                <span
                  style={{
                    color: '#797D88',
                    fontFamily: 'Pretendard',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '24px '
                  }}
                >
                  (선택)
                </span>
              </div>
            }
            description="기업/행사, 교육에서 전달하고 싶은 목적이 있을 경우 작성해주세요."
          >
            <input
              type="text"
              placeholder="예) 환경 보호의 중요성, 팀워크의 소중함"
              value={selectedPurpose}
              onChange={(e) => setValue('purpose', e.target.value)}
              css={PurposeInputField}
            />
          </Card>

          {/* 확인 버튼 */}
          <div css={ActionSection}>
            <Button
              type="primary"
              size="large"
              disabled={!isFormValid || isCreating}
              onClick={() => {
                handleSubmit(onSubmit)();

                track.onClick({
                  clickEventName: ClickEventName.click_newgame_ai_create_bottom,
                  params: {
                    channel_id: appId,
                    genre: selectedGenre,
                    temporalBackground: selectedTemporalBackground,
                    spatialBackground: selectedSpatialBackground,
                    atmosphere: selectedAtmospheres,
                    storyGoal: selectedStoryGoal,
                    storyGoalCustom: selectedStoryGoalCustom,
                    mainEvent: selectedMainEvent,
                    mainEventCustom: selectedMainEventCustom,
                    ending: selectedEnding,
                    purpose: selectedPurpose
                  }
                });
              }}
            >
              {isCreating ? '스토리 생성 중...' : '스토리 만들기'}
            </Button>
          </div>
        </div>

        {/* 플로팅 체크리스트 */}
        <div css={CheckListContainer}>
          <CheckList
            genreChecked={selectedGenre !== null}
            backgroundChecked={
              selectedTemporalBackground !== null && selectedSpatialBackground !== null
            }
            atmosphereChecked={selectedAtmospheres.length > 0}
            storyGoalChecked={selectedStoryGoal !== null}
            mainEventChecked={selectedMainEvent !== null}
            endingChecked={selectedEnding !== null}
            purposeChecked={selectedPurpose.trim().length > 0}
            isAllRequiredChecked={isFormValid}
            onClick={() => {
              track.onClick({
                clickEventName: ClickEventName.click_newgame_ai_create_side,
                params: {
                  channel_id: appId,
                  genre: selectedGenre,
                  temporalBackground: selectedTemporalBackground,
                  spatialBackground: selectedSpatialBackground,
                  atmosphere: selectedAtmospheres,
                  storyGoal: selectedStoryGoal,
                  storyGoalCustom: selectedStoryGoalCustom,
                  mainEvent: selectedMainEvent,
                  mainEventCustom: selectedMainEventCustom,
                  ending: selectedEnding,
                  purpose: selectedPurpose
                }
              });
              handleSubmit(onSubmit)();
            }}
          />
        </div>
      </div>
      <Modal {...modal} />
    </Layout>
  );
}

export default AiConfig;

const Container = css`
  display: flex;
  width: 100%;
  max-width: 1006px;
  justify-content: space-between;
  margin: 0 auto;
`;

const CheckListContainer = css`
  position: sticky;
  top: 76px;
  right: 0;
  max-width: 200px;
  height: 100%;

  /* 1050px 이하에서는 숨김 */
  @media (max-width: 1050px) {
    display: none;
  }
`;

const CardContainer = css`
  max-width: 650px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-left: 180px;

  @media (max-width: 1050px) {
    margin: 0 auto;
  }
`;

const CardBody = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Section = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const SectionTitle = css`
  font-family: 'Pretendard', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #191919;
  margin: 0;
`;

const ButtonGroup = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`;

const ActionSection = css`
  display: flex;
  justify-content: center;
  margin-top: 8px;
  margin-bottom: 90px;
  width: 100%;
`;

const PurposeInputField = css`
  width: 100%;
  padding: 12px;
  border: 1px solid #ededed;
  border-radius: 8px;
  background-color: #fafafa;
  font-family: 'Pretendard', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #0e1017;

  &::placeholder {
    color: #a9a9a9;
  }

  &:focus {
    outline: none;
    border-color: #7800ff;
  }
`;
